import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import RoleDashboard from "@/pages/RoleDashboard";
import Reimbursements from "@/pages/Reimbursements";
import MyWorkAll from "@/pages/MyWorkAll";
import WhoIsResponsible from "@/pages/WhoIsResponsible";
import ReimbursementForm from "@/pages/ReimbursementForm";
import ReimbursementDetail from "@/pages/ReimbursementDetail";
import Bills from "@/pages/Bills";
import BillDetail from "@/pages/BillDetail";
import Tasks from "@/pages/Tasks";
import Inward from "@/pages/Inward";
import Outward from "@/pages/Outward";
import Workers from "@/pages/Workers";
import Reports from "@/pages/Reports";
import Assistant from "@/pages/Assistant";
import Locations from "@/pages/Locations";
import Users from "@/pages/Users";
import CreateOrder from "@/pages/CreateOrder";
import ControlTower from "@/pages/ControlTower";
import OrderFulfilment from "@/pages/OrderFulfilment";
import OrderDetail from "@/pages/OrderDetail";
import Exceptions from "@/pages/Exceptions";
import StockReservation from "@/pages/StockReservation";
import SkuDetail from "@/pages/SkuDetail";
import Roles from "@/pages/Roles";
import DataReset from "@/pages/DataReset";
import ProductHub from "@/pages/ProductHub";
import HubSkuDetail from "@/pages/HubSkuDetail";
import HubCompare from "@/pages/HubCompare";
import ApplicationMaster from "@/pages/ApplicationMaster";
import StockRequests from "@/pages/StockRequests";
import Replenishment from "@/pages/Replenishment";
import HubDashboard from "@/pages/HubDashboard";
import Production from "@/pages/Production";
import Factory from "@/pages/Factory";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import SalesTower from "@/pages/SalesTower";
import Products from "@/pages/Products";
import Samples from "@/pages/Samples";
import SampleDetail from "@/pages/SampleDetail";
import Warehouses from "@/pages/Warehouses";
import Projects from "@/pages/office/Projects";
import Recurring from "@/pages/office/Recurring";
import CommandCenter from "@/pages/CommandCenter";
import Templates from "@/pages/office/Templates";
import OfficeAgeing from "@/pages/office/OfficeAgeing";
import TaskManagement from "@/pages/tm/TaskManagement";
import Standup from "@/pages/tm/Standup";
import Eod from "@/pages/tm/Eod";
import DailyReview from "@/pages/tm/DailyReview";
import Approvals from "@/pages/tm/Approvals";
import TmControlTower from "@/pages/tm/TmControlTower";
import TmReports from "@/pages/tm/TmReports";
import Notifications from "@/pages/tm/Notifications";
import TmArchive from "@/pages/tm/TmArchive";
import TmSettings from "@/pages/tm/TmSettings";
import HrDashboard from "@/pages/hrms/HrDashboard";
import Employees from "@/pages/hrms/Employees";
import EmployeeProfile from "@/pages/hrms/EmployeeProfile";
import Attendance from "@/pages/hrms/Attendance";
import ShiftsLeave from "@/pages/hrms/ShiftsLeave";

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="app-loading">
      <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

const ROLES_MGMT = ["admin", "warehouse_manager", "supervisor"];
const ROLES_ORDER_CREATE = ["admin", "warehouse_manager", "sales"];
const ROLES_CUSTOMER_ACCESS = ["admin", "warehouse_manager", "supervisor", "sales", "accounts"];
const ROLES_PRODUCTION_ACCESS = ["admin", "warehouse_manager", "production"];
const ROLES_MGMT_SALES = ["admin", "warehouse_manager", "supervisor", "sales"];
const ROLES_OFFICE = ["*"];
const ROLES_COMMAND = ["admin", "warehouse_manager"];
const ROLES_ALL = ["admin", "warehouse_manager", "supervisor", "worker", "dispatch", "sales", "accounts", "production", "qc"];

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

function HomeRoute() {
  const { user } = useAuth();
  if (user?.role === "worker") return <Navigate to="/tasks" replace />;
  return <RoleDashboard />;
}

function RequireRole({ roles, children }) {
  const { user } = useAuth();
  if (!roles.includes("*") && !roles.includes(user?.role)) {
    return <Navigate to={user?.role === "worker" ? "/tasks" : "/"} replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Detect OAuth session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route path="/" element={<HomeRoute />} />
        <Route path="/my-dashboard" element={<RoleDashboard />} />
        <Route path="/reimbursements" element={<Reimbursements />} />
        <Route path="/reimbursements/new" element={<ReimbursementForm />} />
        <Route path="/reimbursements/:id/edit" element={<ReimbursementForm />} />
        <Route path="/reimbursements/:id" element={<ReimbursementDetail />} />
        <Route path="/ops-dashboard" element={<RequireRole roles={ROLES_MGMT}><Dashboard /></RequireRole>} />
        <Route path="/control-tower" element={<RequireRole roles={ROLES_MGMT}><ControlTower /></RequireRole>} />
        <Route path="/orders" element={<OrderFulfilment />} />
        <Route path="/orders/new" element={<RequireRole roles={ROLES_ORDER_CREATE}><CreateOrder /></RequireRole>} />
        <Route path="/orders/:billNo" element={<OrderDetail />} />
        <Route path="/sales-tower" element={<RequireRole roles={ROLES_MGMT_SALES}><SalesTower /></RequireRole>} />
        <Route path="/command-center" element={<RequireRole roles={ROLES_COMMAND}><CommandCenter /></RequireRole>} />
        <Route path="/office/my-work" element={<Navigate to="/tm/my" replace />} />
        <Route path="/my-work" element={<MyWorkAll />} />
        <Route path="/who-is-responsible" element={<WhoIsResponsible />} />
        <Route path="/office/team" element={<Navigate to="/tm/team" replace />} />
        <Route path="/office/calendar" element={<Navigate to="/tm?view=calendar" replace />} />
        <Route path="/office/escalations" element={<Navigate to="/tm/escalations" replace />} />
        <Route path="/tm" element={<RequireRole roles={ROLES_OFFICE}><TaskManagement scope="all" /></RequireRole>} />
        <Route path="/tm/my" element={<RequireRole roles={ROLES_OFFICE}><TaskManagement scope="mine" /></RequireRole>} />
        <Route path="/tm/team" element={<RequireRole roles={ROLES_OFFICE}><TaskManagement scope="team" /></RequireRole>} />
        <Route path="/tm/standup" element={<RequireRole roles={ROLES_OFFICE}><Standup /></RequireRole>} />
        <Route path="/tm/eod" element={<RequireRole roles={ROLES_OFFICE}><Eod /></RequireRole>} />
        <Route path="/tm/daily-review" element={<RequireRole roles={ROLES_OFFICE}><DailyReview /></RequireRole>} />
        <Route path="/tm/approvals" element={<RequireRole roles={ROLES_OFFICE}><Approvals tab="approvals" /></RequireRole>} />
        <Route path="/tm/escalations" element={<RequireRole roles={ROLES_OFFICE}><Approvals tab="escalations" /></RequireRole>} />
        <Route path="/tm/control-tower" element={<TmControlTower />} />
        <Route path="/tm/reports" element={<RequireRole roles={ROLES_OFFICE}><TmReports /></RequireRole>} />
        <Route path="/tm/notifications" element={<Notifications />} />
        <Route path="/tm/archive" element={<RequireRole roles={["admin"]}><TmArchive /></RequireRole>} />
        <Route path="/tm/settings" element={<RequireRole roles={["admin"]}><TmSettings /></RequireRole>} />
        <Route path="/hrms" element={<HrDashboard />} />
        <Route path="/hrms/employees" element={<Employees />} />
        <Route path="/hrms/employees/:employeeId" element={<EmployeeProfile />} />
        <Route path="/hrms/attendance" element={<Attendance />} />
        <Route path="/hrms/shifts-leave" element={<ShiftsLeave />} />
        <Route path="/office/projects" element={<RequireRole roles={ROLES_OFFICE}><Projects /></RequireRole>} />
        <Route path="/office/recurring" element={<RequireRole roles={ROLES_OFFICE}><Recurring /></RequireRole>} />
        <Route path="/office/templates" element={<RequireRole roles={ROLES_OFFICE}><Templates /></RequireRole>} />
        <Route path="/office/ageing" element={<RequireRole roles={ROLES_OFFICE}><OfficeAgeing /></RequireRole>} />
        <Route path="/customers" element={<RequireRole roles={ROLES_CUSTOMER_ACCESS}><Customers /></RequireRole>} />
        <Route path="/customers/:code" element={<RequireRole roles={ROLES_CUSTOMER_ACCESS}><CustomerDetail /></RequireRole>} />
        <Route path="/products" element={<RequireRole roles={ROLES_CUSTOMER_ACCESS}><Products /></RequireRole>} />
        <Route path="/samples" element={<RequireRole roles={ROLES_ALL}><Samples /></RequireRole>} />
        <Route path="/samples/:sid" element={<RequireRole roles={ROLES_ALL}><SampleDetail /></RequireRole>} />
        <Route path="/warehouses" element={<RequireRole roles={ROLES_ALL}><Warehouses /></RequireRole>} />
        <Route path="/stock" element={<StockReservation />} />
        <Route path="/stock/sku/:sku" element={<SkuDetail />} />
        <Route path="/exceptions" element={<Exceptions />} />
        <Route path="/production" element={<RequireRole roles={ROLES_PRODUCTION_ACCESS}><Production /></RequireRole>} />
        <Route path="/factory" element={<RequireRole roles={["*"]}><Factory /></RequireRole>} />
        <Route path="/bills" element={<Bills />} />
        <Route path="/bills/:billNo" element={<BillDetail />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/inward" element={<Inward />} />
        <Route path="/outward" element={<Outward />} />
        <Route path="/workers" element={<Workers />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/assistant" element={<Assistant />} />
        <Route path="/locations" element={<Locations />} />
        <Route path="/users" element={<RequireRole roles={["admin"]}><Users /></RequireRole>} />
        <Route path="/roles" element={<RequireRole roles={["admin"]}><Roles /></RequireRole>} />
        <Route path="/data-reset" element={<RequireRole roles={["admin"]}><DataReset /></RequireRole>} />
        <Route path="/hub" element={<ProductHub />} />
        <Route path="/hub/compare" element={<HubCompare />} />
        <Route path="/hub/applications" element={<ApplicationMaster />} />
        <Route path="/hub/stock-requests" element={<StockRequests />} />
        <Route path="/hub/replenishment" element={<Replenishment />} />
        <Route path="/hub/dashboard" element={<HubDashboard />} />
        <Route path="/hub/sku/:sku" element={<HubSkuDetail />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
